/**
 * Canonical TS types for the Monthly Targets feature.
 *
 * Mirrors the backend resources 1:1:
 *   - app/Http/Resources/TargetPeriodResource.php
 *   - app/Http/Resources/TargetTierResource.php
 *   - app/Http/Resources/AgentProgressResource.php
 *   - app/Enums/TargetMetric.php
 *
 * Re-exported via frontend/types/index.ts so consumers can
 * `import { TargetPeriod } from '@/types'` without traversing /lib.
 */

export const TARGET_METRICS = [
  "quotes_accepted_count",
  "quotes_accepted_revenue",
  "leads_converted",
  // EKDent 260907 — the value of treatment a DOCTOR delivered, attributed per
  // invoice line. The three above all measure the SALES assignee.
  "doctor_work_performed",
] as const

export type TargetMetric = (typeof TARGET_METRICS)[number]

export const TARGET_METRIC_LABELS: Record<TargetMetric, string> = {
  quotes_accepted_count: "Quotes Accepted (Count)",
  quotes_accepted_revenue: "Quotes Accepted (Revenue)",
  leads_converted: "Leads Converted",
  doctor_work_performed: "Work Performed (Doctor)",
}

/** Metrics whose values are monetary (formatted with the clinic currency). */
export const MONEY_METRICS: ReadonlySet<string> = new Set<string>([
  "quotes_accepted_revenue",
  "doctor_work_performed",
])

export interface TargetTier {
  id: string
  min_percentage: number
  max_percentage: number | null
  bonus_amount: number
  order: number
}

export interface TargetPeriod {
  id: string
  year: number
  month: number
  metric: TargetMetric
  metric_label: string
  period_label: string
  target_value: number
  is_active: boolean
  is_locked: boolean
  tiers: TargetTier[]
  created_by?: { id: string; full_name: string }
  created_at?: string
  updated_at?: string
}

export interface AgentProgress {
  id: string
  user: { id: string; full_name: string }
  current_value: number
  target_value: number
  percentage: number
  tier_achieved?: TargetTier | null
  bonus_earned: number | null
  status_updates_count: number
  comments_count: number
  quotes_sent_count: number
  days_remaining: number | null
  last_calculated_at: string | null
  period?: TargetPeriod
}

export interface MetricOption {
  value: string
  label: string
}

export interface TargetTierInput {
  min_percentage: number
  max_percentage?: number | null
  bonus_amount: number
  order?: number
}

export interface CreateTargetPeriodData {
  year: number
  month: number
  metric: TargetMetric
  target_value: number
  is_active?: boolean
  tiers?: TargetTierInput[]
}

export type UpdateTargetPeriodData = Partial<CreateTargetPeriodData>

export interface SetTargetTiersData {
  tiers: TargetTierInput[]
}

export interface LeaderboardEntry {
  rank: number
  user: { id: string; full_name: string }
  current_value: number
  percentage: number
  tier_achieved?: TargetTier | null
  bonus_earned: number | null
}
